import { type JSX } from 'react';
import { generatePath, useNavigate } from 'react-router';

import { DocumentApi } from 'Api/Document/DocumentApi';
import { OverflowMenu, OverflowMenuProps } from 'Components/Buttons/OverflowMenu';
import { useCachedData } from 'Components/Context/CachedDataContext';
import { UploadedFileAndState } from 'Components/Files/UploadedFileAndState';
import { ColumnConfig, ColumnType } from 'Components/Table/Columns';
import { FilterConfig, FilterType } from 'Components/Table/Components/Filters';
import { Table } from 'Components/Table/Table';
import { Text } from 'Components/Text/Text';
import { FutureGovernanceVersionTooltip } from 'Components/Tooltips/FutureGovernanceVersionTooltip';
import { ICON_DOWNLOAD, ICON_EDIT_MODIFY_UPDATE, ICON_NEW_TAB, ICON_VIEW } from 'Config/Icons';
import { GOVERNANCE_DETAILS } from 'Config/PathTemplates';
import { downloadDocument } from 'Helpers/FileUtils';
import { FileState, UploadedFile } from 'Models/Files';
import { GovernanceContentType, GovernanceVersion, TextBasedGovernanceVersion, getHumanReadableGovernanceType } from 'Models/Governance';
import { OperationalControl } from 'Models/OperationalControls';

import styles from './GovernanceLibraryTable.module.css';

export interface GovernanceLibraryTableProps {
    documentApi: DocumentApi;
    governanceItems: GovernanceVersion[];
    governanceItemsAreManageable: boolean;
    displayGovernanceTextModal: (governance: TextBasedGovernanceVersion) => void;
    displayAssociatedControlsModal: (controls: OperationalControl[]) => void;
    controlIdentifier?: string;
    openExternalUrl: (url: string) => void;
}

export const GovernanceLibraryTable = (props: GovernanceLibraryTableProps): JSX.Element => {
    const cachedData = useCachedData();
    const navigate = useNavigate();

    const downloadGovernanceFile = async (file: UploadedFile): Promise<void> => {
        try {
            downloadDocument(props.documentApi, file);
        } catch (error) {
            console.log(error);
        }
    };

    const getOverflowMenuForRow = (governanceItem: GovernanceVersion): JSX.Element => {
        const menuText = getHumanReadableGovernanceType(governanceItem.type);
        const manageLocation = props.controlIdentifier !== undefined ? `${generatePath(GOVERNANCE_DETAILS, { governance_id: governanceItem.id })}?controlId=${encodeURIComponent(props.controlIdentifier!)}` : generatePath(GOVERNANCE_DETAILS, { governance_id: governanceItem.id });
        const manageVerb = props.governanceItemsAreManageable ? 'Manage' : 'View';
        const manageIcon = props.governanceItemsAreManageable ? ICON_EDIT_MODIFY_UPDATE : ICON_VIEW;

        const overflowItemsProp: OverflowMenuProps = {
            overflowItems: [
                {
                    text: `${manageVerb} ${menuText}`,
                    onClickAction: () => navigate(manageLocation),
                    icon: manageIcon,
                },
            ],
            accessibilityTitle: `${governanceItem.title} Menu`,
        };

        if (props.governanceItemsAreManageable) {
            if (governanceItem.content_type === GovernanceContentType.DOCUMENT && governanceItem.file.file_state === FileState.PASSED) {
                overflowItemsProp.overflowItems.push({
                    text: `Download ${menuText}`,
                    onClickAction: () => downloadGovernanceFile(governanceItem.file),
                    icon: ICON_DOWNLOAD,
                });
            } else if (governanceItem.content_type === GovernanceContentType.TEXT) {
                overflowItemsProp.overflowItems.push({
                    text: `View ${menuText}`,
                    onClickAction: () => props.displayGovernanceTextModal(governanceItem),
                    icon: ICON_VIEW,
                });
            } else if (governanceItem.content_type === GovernanceContentType.EXTERNAL_URL) {
                overflowItemsProp.overflowItems.push({
                    text: `Open ${menuText} Link`,
                    onClickAction: () => props.openExternalUrl(governanceItem.external_url),
                    icon: ICON_NEW_TAB,
                });
            }
        }

        return <OverflowMenu {...overflowItemsProp} />;
    };

    const filters: FilterConfig<GovernanceVersion>[] = [
        {
            type: FilterType.USERS,
            id: 'ownerFilter',
            label: 'Filter by Owner',
            getUserIdsForRow: (item) => [item.owner_user_id],
        },
    ];

    const columns: ColumnConfig<GovernanceVersion>[] = [
        {
            type: ColumnType.CUSTOM,
            name: 'Title',
            getValue: (item) => item.title,
            sortComparator: (a, b) => a.localeCompare(b),
            getTableCellContent: (item) => (
                <>
                    <div className={styles.governanceTitleContainer}>
                        {item.content_type === GovernanceContentType.DOCUMENT ? <UploadedFileAndState unclickable title={item.title} documentApi={props.documentApi} file={item.file} /> : <Text noStyles>{item.title}</Text>}
                        <div className={styles.tooltipContainer}>
                            <FutureGovernanceVersionTooltip version={item} />
                        </div>
                    </div>
                    <Text variant="Text3" noStyles color="darkGray">
                        {getHumanReadableGovernanceType(item.type)}
                    </Text>
                </>
            ),
        },
        {
            type: ColumnType.USER,
            name: 'Owner',
            getUserId: (item) => item.owner_user_id,
            users: cachedData.users,
        },
        {
            type: ColumnType.MULTIPLE_CONTROLS,
            name: 'Controls',
            getControls: (item) => item.associated_controls,
            onClick: props.displayAssociatedControlsModal,
        },
        {
            type: ColumnType.DATE,
            name: 'Effective Date',
            getDateString: (item) => item.effective_date,
        },
    ];

    return <Table<GovernanceVersion> columns={columns} defaultSortColumn="Title" filters={filters} getUniqueIdForRow={(item) => item.id} rowsData={props.governanceItems} getOverflowMenuForRow={getOverflowMenuForRow} />;
};
